import request from "./request";
import { message } from "antd";
import {
  filterV3ProductsByCode,
  type V3Product,
} from "./v3Compat";

// 从环境变量或配置中获取 API 基础地址
const configuredAPIBaseURL = process.env.REACT_APP_API_BASE_URL?.trim();
const API_BASE_URL =
  configuredAPIBaseURL === "__SAME_ORIGIN__"
    ? window.location.origin
    : configuredAPIBaseURL || "https://api.ai.zhheo.com";

interface PaymentParams {
  product_id: number;
  product_count: number;
  project_id?: number;
}

interface RedeemParams {
  recharge_code: string[];
  project_id?: number;
}

/**
 * 充值商品的接口定义
 * 用于 getProducts 返回的商品列表类型
 */
interface Product {
  id: number;
  code: string;
  type: string;
  vip_level?: number;
  name: string;
  price: number; // 价格，单位：分
  token_amount?: number;
  // ... 其他字段
}

/**
 * 充值码返回数据接口
 */
interface RedeemResponseData {
  success_code: string;
  recharge_token: string;
  token: string;
  fail_list:
    | {
        fail_code: string;
        recharge_token: string;
        msg: string;
      }[]
    | null;
}

/**
 * 微信支付创建订单响应接口
 */
interface WechatOrderResponse {
  order_id: string; // 订单ID
  code_url: string; // 二维码链接
  amount: number; // 订单金额(元)
}

/**
 * 退款请求参数接口
 */
interface RefundParams {
  order_id: string; // 订单号
  refund_amount: number; // 退款金额(元)
  refund_reason: string; // 退款原因
}

/**
 * 退款响应数据接口
 */
interface RefundResponseData {
  success: boolean; // 退款是否成功
  refund_id: string; // 退款ID
  message: string; // 退款消息
  pay_type: string; // 支付类型
  trade_no: string; // 交易号
  refund_no: string; // 退款号
}

/**
 * 微信支付查询订单响应接口
 */
interface WechatQueryResponse {
  order_id: string; // 订单ID
  pay_status: number; // 支付状态：0-未支付，1-已支付
  pay_id: string; // 支付ID
  amount: number; // 订单金额
  create_time: number; // 创建时间戳
}

/**
 * API响应接口
 */
interface ApiResponse<T> {
  code: number;
  msg: string;
  data: T;
}

interface V3ProductListResponse {
  list: V3Product[];
  total: number;
}

export const payment = {
  // 获取支付链接
  getAlipayUrl: async (
    params: PaymentParams,
    token: string | null
  ): Promise<string | null> => {
    if (!token) {
      message.error("未登录或登录已过期");
      return null;
    }

    try {
      // 确保 project_id 存在，PostChat 充值时为 0
      const payParams = {
        ...params,
        project_id: params.project_id ?? 0, // 如果没有传入 project_id，则默认为 0
      };

      const response = await request.post(
        `${API_BASE_URL}/api/v3/payment/alipay/create`,
        payParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200 && response.data.data.pay_url) {
        return response.data.data.pay_url;
      } else {
        message.error("获取支付链接失败");
        return null;
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "支付失败，请重试");
      return null;
    }
  },

  // 创建微信支付订单
  createWechatOrder: async (
    params: PaymentParams,
    token: string | null
  ): Promise<WechatOrderResponse | null> => {
    if (!token) {
      message.error("未登录或登录已过期");
      return null;
    }

    try {
      // 确保 project_id 存在，PostChat 充值时为 0
      const payParams = {
        ...params,
        project_id: params.project_id ?? 0, // 如果没有传入 project_id，则默认为 0
      };

      const response = await request.post<ApiResponse<WechatOrderResponse>>(
        `${API_BASE_URL}/api/v3/payment/wechat/create`,
        payParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        message.error(response.data.msg || "创建微信支付订单失败");
        return null;
      }
    } catch (error: any) {
      message.error(
        error.response?.data?.msg || "创建微信支付订单失败，请重试"
      );
      return null;
    }
  },

  // 查询微信支付订单状态
  queryWechatOrder: async (
    orderId: string,
    token: string | null,
    showError: boolean = true
  ): Promise<boolean> => {
    if (!token) {
      if (showError) {
        message.error("未登录或登录已过期");
      }
      return false;
    }

    try {
      const response = await request.get<ApiResponse<WechatQueryResponse>>(
        `${API_BASE_URL}/api/v3/payment/wechat/query/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        // 根据 pay_status 判断支付状态：1 表示已支付，0 表示未支付
        return response.data.data.pay_status === 1;
      } else {
        if (showError) {
          message.error(response.data.msg || "查询微信支付订单状态失败");
        }
        return false;
      }
    } catch (error: any) {
      if (showError) {
        message.error(error.response?.data?.msg || "查询微信支付订单状态失败");
      }
      console.error("查询微信支付订单状态失败:", error);
      return false;
    }
  },

  // 验证支付结果
  verifyPayment: async (
    searchParams: string,
    token: string | null
  ): Promise<boolean> => {
    if (!token) {
      message.error("未登录或登录已过期");
      return false;
    }

    try {
      const response = await request.get(
        `${API_BASE_URL}/api/v3/payment/alipay/return${searchParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        message.success("支付成功");
        return true;
      } else {
        message.error(response.data.msg || "支付验证失败");
        return false;
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "支付验证失败");
      return false;
    }
  },

  // 兑换码充值
  redeemCode: async (
    params: RedeemParams,
    token: string | null
  ): Promise<RedeemResponseData | null> => {
    if (!token) {
      message.error("未登录或登录已过期");
      return null;
    }

    try {
      const response = await request.post<ApiResponse<RedeemResponseData>>(
        `${API_BASE_URL}/api/v3/recharge/code`,
        params,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        message.error(response.data.msg || "兑换失败");
        return null;
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "兑换失败，请重试");
      return null;
    }
  },

  // 统一退款接口
  refundOrder: async (
    params: RefundParams,
    token: string | null
  ): Promise<RefundResponseData | null> => {
    if (!token) {
      message.error("未登录或登录已过期");
      return null;
    }

    try {
      const response = await request.post<ApiResponse<RefundResponseData>>(
        `${API_BASE_URL}/api/v3/admin/refund`,
        params,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        return response.data.data;
      } else {
        message.error(response.data.msg || "退款失败");
        return null;
      }
    } catch (error: any) {
      message.error(error.response?.data?.msg || "退款失败，请重试");
      return null;
    }
  },

  // 获取充值商品列表
  getProducts: async (
    code: "P" | "S",
    token: string | null
  ): Promise<Product[]> => {
    if (!token) {
      message.error("未登录或登录已过期");
      return [];
    }

    try {
      const response = await request.get<ApiResponse<V3ProductListResponse>>(
        `${API_BASE_URL}/api/v3/products`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.code === 200) {
        return filterV3ProductsByCode(response.data.data.list || [], code);
      }
      return [];
    } catch (error) {
      message.error("获取商品列表失败");
      return [];
    }
  },

  getRewardProducts: async (token: string | null): Promise<Product[]> => {
    if (!token) {
      message.error("未登录或登录已过期");
      return [];
    }

    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/recharge/products`, {
        params: {
          type: "reward",
          is_on_sale: "all",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        const list = Array.isArray(response.data.data?.list)
          ? response.data.data.list
          : Array.isArray(response.data.data)
          ? response.data.data
          : [];
        return list;
      }
      return [];
    } catch (error) {
      message.error("获取奖励商品失败");
      return [];
    }
  },
};
